import { useAmplitude } from "@getsoren/shared-module";
import { useEffect } from "react";
import useUser from "@/hooks/useUser";

/**
 * Syncs the authenticated user to Amplitude: identify + setGroup on login and on refresh
 * when a user is already in localStorage. Logout is handled by `useUser.signOut` which calls reset.
 */
const AmplitudeUserSync = () => {
  const { id, email, entity, tenantId, tenantName } = useUser();
  const { identify, setGroup } = useAmplitude();
  const role = entity?.role?.roleName;

  /**
   * Push the authenticated user into Amplitude (identify + tenant group). Runs on login AND on refresh
   * when a user is already in localStorage — both resolve to the same effect: we have an `id`/`tenantId`,
   * so we re-identify. Deps are value-based (strings), so React dedupes when nothing actually changed,
   * and rehydration of `userLocal` by `useUser` (e.g. permissions refresh) doesn't retrigger network calls.
   */
  useEffect(() => {
    // Bail until we know who the user is AND which tenant they belong to — Amplitude requires both to associate the user to the group.
    if (!(id && tenantId)) {
      return;
    }

    // `user_id` is duplicated as a user property on purpose: the ticket lists it in the identify table alongside the Amplitude user id passed as first arg, so both the canonical id and the searchable property are set.
    // `tenant_name` is duplicated from the group property on purpose: group properties are not queryable
    // through the Dashboard REST API without the Accounts add-on, whereas a user property is (Cockpit's tenant picker reads it).
    identify(id, {
      email: email ?? "",
      role: role ?? "",
      tenant_id: tenantId,
      tenant_name: tenantName ?? "",
      user_id: id,
    });

    // Bind the user to its tenant group so Amplitude Accounts can aggregate behavior per organization (requires Growth+ plan to actually surface group properties in the UI).
    setGroup("tenant", tenantId, { organization_name: tenantName ?? "" });
  }, [id, email, role, tenantId, tenantName, identify, setGroup]);

  return null;
};

export default AmplitudeUserSync;
